import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit2, Plus, DollarSign, ChevronDown } from 'lucide-react';
import { getFareConfigs, createFareConfig, updateFareConfig } from '../api/fareConfig';
import { getVehicleTypes } from '../api/vehicleTypes';
import { useToast } from '../context/ToastContext';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import { Skeleton } from '../components/ui/Skeleton';
import { getErrorMessage } from '../lib/utils';

const EMPTY_FORM = { vehicle_type_id: '', base_fare: '', per_km_rate: '', per_min_rate: '', min_fare: '', access_fee: '', service_fee: '', is_active: true };

function FareForm({ initial = EMPTY_FORM, mode, vehicleTypes, onSubmit, loading, onClose }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});

  function set(key, val) { setForm((f) => ({ ...f, [key]: val })); }

  // Same rules the backend enforces (fare_rule_errors in
  // vandigo_backend/schemas/fare_config_schema.py) — checked here first so
  // the admin sees the problem on the right field before saving.
  function validate() {
    const e = {};
    const isBlank = (v) => v === '' || v === null || v === undefined;
    const num = (v) => Number(v);
    if (mode === 'create' && !form.vehicle_type_id) e.vehicle_type_id = 'Vehicle type is required';

    if (isBlank(form.base_fare)) e.base_fare = 'Required';
    else if (num(form.base_fare) <= 0) e.base_fare = 'Must be greater than 0';

    if (isBlank(form.per_km_rate)) e.per_km_rate = 'Required';
    else if (num(form.per_km_rate) <= 0) e.per_km_rate = 'Must be greater than 0';

    if (isBlank(form.per_min_rate)) e.per_min_rate = 'Required';
    else if (num(form.per_min_rate) <= 0) e.per_min_rate = 'Must be greater than 0';

    // 0 is a valid fee — only blank or negative is rejected.
    if (isBlank(form.access_fee)) e.access_fee = 'Required';
    else if (num(form.access_fee) < 0) e.access_fee = 'Cannot be negative';

    if (isBlank(form.service_fee)) e.service_fee = 'Required';
    else if (num(form.service_fee) < 0) e.service_fee = 'Cannot be negative';

    // Driver earning = fare − service fee − access fee, and every fare is at
    // least the base fare, so both fees together must stay below it.
    if (!e.base_fare && !e.access_fee && !e.service_fee
        && num(form.access_fee) + num(form.service_fee) >= num(form.base_fare)) {
      e.service_fee = 'Access fee + service fee must be less than base fare';
    }
    return e;
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const payload = {
      base_fare: Number(form.base_fare),
      per_km_rate: Number(form.per_km_rate),
      per_min_rate: Number(form.per_min_rate),
      // Minimum fare is always the same as the base fare (the backend
      // enforces this too), so there is never a "minimum fare adjustment".
      min_fare: Number(form.base_fare),
      access_fee: Number(form.access_fee),
      service_fee: Number(form.service_fee),
      is_active: form.is_active,
    };
    if (mode === 'create') payload.vehicle_type_id = Number(form.vehicle_type_id);
    onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {mode === 'create' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Vehicle Type<span className="text-red-500 ml-0.5">*</span></label>
          <div className="relative">
            <select
              value={form.vehicle_type_id}
              onChange={(e) => set('vehicle_type_id', e.target.value)}
              className="appearance-none block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 pl-3 pr-8 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="" disabled>Select a vehicle type…</option>
              {vehicleTypes.length > 0 ? (
                vehicleTypes.map((type) => (
                  <option key={type.id} value={type.id} className="capitalize">{type.name}</option>
                ))
              ) : (
                <option value="" disabled>Loading vehicle types…</option>
              )}
            </select>
            <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
          {errors.vehicle_type_id && <p className="mt-1 text-xs text-red-500">{errors.vehicle_type_id}</p>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Input label="Base Fare (₹)" required type="number" min="0" step="0.01" value={form.base_fare} onChange={(e) => set('base_fare', e.target.value)} error={errors.base_fare} placeholder="30" />
        <Input label="Per KM Rate (₹)" required type="number" min="0" step="0.01" value={form.per_km_rate} onChange={(e) => set('per_km_rate', e.target.value)} error={errors.per_km_rate} placeholder="12" />
        <Input label="Per Min Rate (₹)" required type="number" min="0" step="0.01" value={form.per_min_rate} onChange={(e) => set('per_min_rate', e.target.value)} error={errors.per_min_rate} placeholder="1.5" />
        <Input label="Min Fare (₹)" type="number" value={form.base_fare} disabled readOnly placeholder="Same as base fare" />
        <Input label="Access Fee (₹)" required type="number" min="0" step="0.01" value={form.access_fee} onChange={(e) => set('access_fee', e.target.value)} error={errors.access_fee} placeholder="3" />
        <Input label="Service Fee (₹)" required type="number" min="0" step="0.01" value={form.service_fee} onChange={(e) => set('service_fee', e.target.value)} error={errors.service_fee} placeholder="3" />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
        Min Fare is always the same as Base Fare. Access fee + service fee must be less than the base fare.
      </p>
      <div className="flex items-center gap-3">
        <input type="checkbox" id="fc_active" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} className="rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
        <label htmlFor="fc_active" className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</label>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" loading={loading}>Save Config</Button>
      </div>
    </form>
  );
}

function FareCard({ config, onEdit }) {
  // Configs saved before the "min fare = base fare" rule may still differ;
  // the backend keeps charging max(fare, min_fare) until they're re-saved.
  const minFareOutOfSync = Number(config.min_fare) !== Number(config.base_fare);
  const rows = [
    ['Base Fare', `₹${config.base_fare}`],
    ['Per KM', `₹${config.per_km_rate}`],
    ['Per Minute', `₹${config.per_min_rate}`],
    ['Min Fare', `₹${config.min_fare}`],
    ['Access Fee', `₹${config.access_fee}`],
    ['Service Fee', `₹${config.service_fee}`],
  ];

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="rounded-xl bg-amber-100 dark:bg-amber-900/30 p-2.5">
            <DollarSign size={18} className="text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 capitalize">{config.vehicle_type}</h3>
            <Badge variant={config.is_active ? 'success' : 'default'} className="mt-0.5">
              {config.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
        <Button size="xs" variant="secondary" icon={Edit2} onClick={() => onEdit(config)}>Edit</Button>
      </div>
      <dl className="space-y-2">
        {rows.map(([label, val]) => (
          <div key={label} className="flex justify-between items-center py-1.5 border-b border-gray-100 dark:border-gray-700">
            <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
            <dd className="text-sm font-semibold text-gray-900 dark:text-gray-100">{val}</dd>
          </div>
        ))}
      </dl>
      {minFareOutOfSync && (
        <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          Min Fare should equal Base Fare. Set the Base Fare you want and save to fix it.
        </p>
      )}
    </Card>
  );
}

export default function FareConfig() {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [modal, setModal] = useState(() => (searchParams.get('action') === 'create' ? { mode: 'create' } : null));

  const { data, isLoading } = useQuery({
    queryKey: ['fare-config'],
    queryFn: () => getFareConfigs().then((r) => r.data),
  });

  const { data: vehicleTypesData } = useQuery({
    queryKey: ['vehicle-types'],
    queryFn: () => getVehicleTypes().then((r) => r.data),
  });

  const vehicleTypes = Array.isArray(vehicleTypesData) ? vehicleTypesData : [];
  const configs = Array.isArray(data) ? data : (data?.items ?? data?.fare_configs ?? []);

  const create = useMutation({
    mutationFn: createFareConfig,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fare-config'] }); addToast('Fare config created!', 'success'); setModal(null); },
    onError: (err) => addToast(getErrorMessage(err, 'Creation failed'), 'error'),
  });

  const update = useMutation({
    mutationFn: ({ vehicle_type, ...rest }) => updateFareConfig(vehicle_type, rest),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fare-config'] }); addToast('Config updated', 'success'); setModal(null); },
    onError: (err) => addToast(getErrorMessage(err, 'Update failed'), 'error'),
  });

  function handleEdit(config) {
    setModal({
      mode: 'edit',
      config,
      initial: {
        vehicle_type: config.vehicle_type,
        base_fare: String(config.base_fare),
        per_km_rate: String(config.per_km_rate),
        per_min_rate: String(config.per_min_rate),
        min_fare: String(config.min_fare),
        access_fee: String(config.access_fee),
        service_fee: String(config.service_fee),
        is_active: config.is_active,
      },
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Fare Configuration</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Pricing rules per vehicle type</p>
        </div>
        <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>Add Config</Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <Skeleton className="h-6 w-1/2 mb-4" />
              {Array.from({ length: 4 }).map((__, j) => <Skeleton key={j} className="h-4 mb-2" />)}
            </Card>
          ))}
        </div>
      ) : configs.length === 0 ? (
        <Card className="py-12 text-center text-gray-400">No fare configs yet. Add one to get started.</Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {configs.map((c) => <FareCard key={c.vehicle_type ?? c.id} config={c} onEdit={handleEdit} />)}
        </div>
      )}

      <Modal
        isOpen={!!modal}
        onClose={() => setModal(null)}
        title={modal?.mode === 'edit' ? `Edit — ${modal?.config?.vehicle_type}` : 'Add Fare Config'}
        size="md"
      >
        {modal && (
          <FareForm
            initial={modal.initial ?? EMPTY_FORM}
            mode={modal.mode}
            vehicleTypes={vehicleTypes}
            loading={modal.mode === 'edit' ? update.isPending : create.isPending}
            onClose={() => setModal(null)}
            onSubmit={(formData) => {
              if (modal.mode === 'edit') update.mutate({ vehicle_type: modal.config.vehicle_type, ...formData });
              else create.mutate(formData);
            }}
          />
        )}
      </Modal>
    </div>
  );
}
